import { useState } from 'react';
import { Bell, MessageSquare, Minus, Pencil, Plus, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';
import { CopyButton } from '@/components/ui/CopyButton';
import { isbnAnzeige } from '@/lib/isbn';
import { Cover } from '@/components/ui/Cover';
import { ErwaehnungTextarea } from '@/components/ui/ErwaehnungTextarea';
import { BeschaffungErfassen } from './BeschaffungErfassen';
import type { PositionInput } from '@/types/bestellung';
import { FOKUS_RING } from '@/lib/fokus';

type Props = {
  position: PositionInput;
  onUpdate: (patch: Partial<PositionInput>) => void;
  onRemove: () => void;
};

// Hier steht bewusst KEIN Hinweis auf einen unbekannten Verlag: die
// Zuordnung macht der Server beim Speichern (A6). Vor dem Speichern ist
// verlag_id immer leer — der Hinweis waere also nicht "Verlag unbekannt",
// sondern "noch nicht gespeichert". In der Detailansicht, wo der Wert
// feststeht, steht er weiterhin (PositionRow).
export function PositionCard({ position, onUpdate, onRemove }: Props) {
  const [editTitel, setEditTitel] = useState(false);
  const [titelEntwurf, setTitelEntwurf] = useState(position.titel);
  const [editNotiz, setEditNotiz] = useState(false);
  const [notizEntwurf, setNotizEntwurf] = useState(position.notiz ?? '');

  // A1: Nur was ein Mensch eingetippt hat, darf ein Mensch korrigieren.
  // Ein Titel aus dem VLB ist ein Katalogwert — wer ihn hier
  // überschreibt, legt eine zweite Wahrheit neben den Katalog
  // (Designprinzip 4). Dieselbe Grenze zieht der Server.
  const titelAenderbar = position.ist_freitext;

  function titelUebernehmen(): void {
    const t = titelEntwurf.trim();
    // Ein leerer Titel ist keine Korrektur, sondern ein Verlust: die
    // Position stünde danach namenlos in der Liste.
    if (t === '') return;
    onUpdate({ titel: t });
    setEditTitel(false);
  }

  return (
    <div className="flex gap-3 rounded-md border border-line bg-cream p-3">
      <Cover isbn={position.isbn} />

      {/* min-w-0: ein Flex-Element schrumpft sonst nicht unter die Breite
          seines Inhalts, und ein langer Titel drückt die Karte bei
          390 Pixeln über den Rand (K11). */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {editTitel ? (
          <div className="space-y-1">
            <input
              type="text"
              autoFocus
              value={titelEntwurf}
              maxLength={500}
              onChange={(e) => setTitelEntwurf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  titelUebernehmen();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setTitelEntwurf(position.titel);
                  setEditTitel(false);
                }
              }}
              aria-label="Titel der Position"
              className={`min-h-[44px] block w-full min-w-0 rounded-md border border-line bg-cream px-3 py-2.5 font-serif text-sm text-ink ${FOKUS_RING}`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={titelUebernehmen}
                disabled={titelEntwurf.trim() === ''}
                className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark disabled:text-ink-faint"
              >
                Übernehmen
              </button>
              <button
                type="button"
                onClick={() => {
                  setTitelEntwurf(position.titel);
                  setEditTitel(false);
                }}
                className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            {titelAenderbar ? (
              // Der Titel selbst ist der Knopf — auf einem Tablet trifft
              // man den Text leichter als ein Stift-Symbol daneben. Das
              // Symbol steht trotzdem da, sonst sieht niemand, dass hier
              // etwas zu ändern ist (kein Hover auf Touch-Geräten).
              <button
                type="button"
                onClick={() => {
                  setTitelEntwurf(position.titel);
                  setEditTitel(true);
                }}
                className="flex min-h-[44px] min-w-0 flex-1 items-center gap-1.5 text-left font-serif text-sm text-ink hover:text-akzent"
                aria-label={`Titel „${position.titel}" ändern`}
              >
                <span className="min-w-0 break-words">{position.titel}</span>
                <Pencil aria-hidden className="mt-0.5 h-3 w-3 shrink-0 text-ink-faint" />
              </button>
            ) : (
              <p className="min-w-0 flex-1 break-words font-serif text-sm text-ink">
                {position.titel}
              </p>
            )}
            {position.ist_freitext && (
              /* E12: hiess „Freitext". Seit E9 kann eine handerfasste
                 Position eine ISBN tragen — „Freitext" behauptete dann
                 „ohne Nummer", und daneben stand die Nummer. Das Merkzeichen
                 sagt, WOHER die Titeldaten kommen, nicht ob es eine ISBN
                 gibt. */
              <span className="shrink-0 rounded bg-gold-soft px-2 py-0.5 text-xs text-gold-dark">
                von Hand
              </span>
            )}
          </div>
        )}
        {(position.autor || position.verlag_text) && (
          <p className="text-xs text-ink-faint">
            {[position.autor, position.verlag_text].filter(Boolean).join(' · ')}
          </p>
        )}
        {position.isbn && (
          <div className="flex items-center gap-1 text-xs text-ink-faint">
            <span className="tabular-nums">ISBN {isbnAnzeige(position.isbn)}</span>
            <CopyButton value={position.isbn} label={`ISBN ${position.isbn} kopieren`} />
          </div>
        )}

        {/* A3: Die Notiz entsteht am Telefon im selben Atemzug wie die
            Position („Kundin fragt nach der gebundenen Ausgabe"). Bis
            hierher ging sie erst nach dem Speichern — und was erst später
            geht, wird oft gar nicht mehr gemacht. */}
        {editNotiz ? (
          <div className="mt-1 space-y-1">
            <ErwaehnungTextarea
              value={notizEntwurf}
              onChange={setNotizEntwurf}
              rows={2}
              placeholder="Notiz zur Position — mit @ jemanden erwähnen"
              className="w-full rounded-md border border-line bg-cream px-2 py-1 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  onUpdate({ notiz: notizEntwurf.trim() === '' ? null : notizEntwurf });
                  setEditNotiz(false);
                }}
                className="inline-flex min-h-[44px] items-center text-xs text-akzent hover:text-akzent-stark"
              >
                Übernehmen
              </button>
              <button
                type="button"
                onClick={() => {
                  setNotizEntwurf(position.notiz ?? '');
                  setEditNotiz(false);
                }}
                className="inline-flex min-h-[44px] items-center text-xs text-ink-soft hover:text-coral"
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : position.notiz ? (
          <button
            type="button"
            onClick={() => {
              setNotizEntwurf(position.notiz ?? '');
              setEditNotiz(true);
            }}
            className="flex min-h-[44px] w-full items-center text-left text-xs italic text-ink-soft hover:text-akzent"
          >
            {position.notiz}
          </button>
        ) : null}

        {/* A4: Der Weg, über den beschafft wird — schon jetzt, nicht erst
            nach dem Speichern. */}
        <BeschaffungErfassen
          menge={position.menge}
          verlagVorschlag={position.verlag_text}
          eintraege={position.beschaffungen ?? []}
          onChange={(b) => onUpdate({ beschaffungen: b })}
        />

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-md border border-line">
            <button
              type="button"
              onClick={() => onUpdate({ menge: Math.max(1, position.menge - 1) })}
              // Echte 44 Pixel: die beiden Zählknöpfe stoßen aneinander,
              // eine unsichtbare Trefferfläche würde überlappen.
              className="inline-flex h-11 w-11 items-center justify-center text-ink-soft hover:bg-cream-dark"
              aria-label="Menge verringern"
            >
              <Minus className="h-3 w-3" />
            </button>
            <span className="w-8 text-center text-sm tabular-nums">{position.menge}</span>
            <button
              type="button"
              onClick={() => onUpdate({ menge: Math.min(999, position.menge + 1) })}
              className="inline-flex h-11 w-11 items-center justify-center text-ink-soft hover:bg-cream-dark"
              aria-label="Menge erhöhen"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>

          {/* A56 (#135): „sagt mir Bescheid, wenn DIESER Titel da ist."
              WARUM HIER UND NICHT IM AUFKLAPP-BEREICH DER BESTELLUNG:
              Der Wunsch gilt EINEM Titel; im Bereich der Bestellung
              müsste man ihn einer Position zuordnen, und dafür bräuchte
              es eine Liste neben der Liste. Als Schalter in der Reihe
              der kleinen Handgriffe — neben Menge, Notiz und
              Entfernen — überlädt er die Zeile nicht: Er ist ein
              Sinnbild unter Sinnbildern und zeigt seinen Zustand über
              `aria-pressed` und die Färbung.
              Die Glocke steht für den WUNSCH, nicht für einen Versand:
              Das System verschickt nichts (Designprinzip 3). */}
          <button
            type="button"
            onClick={() => onUpdate({ nachricht_wunsch: !position.nachricht_wunsch })}
            aria-pressed={position.nachricht_wunsch}
            className={clsx(
              'inline-flex h-11 w-11 items-center justify-center rounded',
              position.nachricht_wunsch
                ? 'bg-akzent-sanft text-akzent-stark'
                : 'text-ink-faint hover:text-akzent'
            )}
            aria-label={
              position.nachricht_wunsch
                ? 'Nachricht gewünscht, wenn dieser Titel da ist — abwählen'
                : 'Nachricht gewünscht, wenn dieser Titel da ist'
            }
            title="Kundin wünscht Nachricht, sobald dieser Titel da ist"
          >
            <Bell className="h-4 w-4" />
          </button>

          {!editNotiz && position.notiz === null && (
            <button
              type="button"
              onClick={() => {
                setNotizEntwurf('');
                setEditNotiz(true);
              }}
              className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-faint hover:text-akzent"
              aria-label="Notiz hinzufügen"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            onClick={onRemove}
            className={clsx(
              'ml-auto inline-flex h-11 w-11 items-center justify-center rounded text-ink-faint',
              'hover:bg-coral-soft hover:text-coral'
            )}
            aria-label="Position entfernen"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
